import { useState, useMemo } from "react";
import { endImpersonation, isImpersonating } from "@/lib/impersonation";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { LogOut, User, Link2, Globe, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import ConnectionsContent from "@/components/connections-content";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

const ALL_TIMEZONES: string[] = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [
      "UTC",
      "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
      "America/Toronto", "America/Vancouver", "America/Winnipeg",
      "Europe/London", "Europe/Paris", "Europe/Berlin",
      "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Singapore",
      "Asia/Shanghai", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland",
    ];
  }
})();

function getTimezoneLabel(tz: string): string {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(now);
    const abbr = parts.find(p => p.type === "timeZoneName")?.value || "";
    const offset = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset",
    }).formatToParts(now).find(p => p.type === "timeZoneName")?.value || "";
    return `${tz.replace(/_/g, " ")} (${abbr}, ${offset})`;
  } catch {
    return tz;
  }
}

interface ProfileProps {
  user: { id: number; email: string; name: string; timezone: string | null };
  onLogout: () => void;
  onUserUpdate?: () => void;
}

export default function Profile({ user, onLogout, onUserUpdate }: ProfileProps) {
  const [activeTab, setActiveTab] = useState<"profile" | "connections">("profile");
  const [tzSearch, setTzSearch] = useState("");
  const [tzOpen, setTzOpen] = useState(false);

  const { data: accounts = [] } = useQuery<Array<{ id: number; timezone?: string }>>({
    queryKey: ["/api/ad-accounts"],
    retry: false,
  });

  const defaultTz = accounts.length > 0 && accounts[0].timezone ? accounts[0].timezone : "UTC";
  const currentTz = user.timezone || defaultTz;

  const timezoneMutation = useMutation({
    mutationFn: async (timezone: string) => {
      const res = await fetch("/api/auth/timezone", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timezone }),
      });
      if (!res.ok) throw new Error("Failed to update timezone");
      return res.json();
    },
    onSuccess: () => {
      onUserUpdate?.();
    },
  });

  const filteredTimezones = useMemo(() => {
    const q = tzSearch.toLowerCase();
    if (!q) return ALL_TIMEZONES;
    return ALL_TIMEZONES.filter(tz =>
      tz.toLowerCase().includes(q) || getTimezoneLabel(tz).toLowerCase().includes(q)
    );
  }, [tzSearch]);

  const handleLogout = async () => {
    // v7.54 — inside an admin "Login as" window, sign-out ends that session only.
    if (isImpersonating()) { await endImpersonation(); return; }
    // credentials: 'include' is required — without the session cookie the
    // server can't destroy the session and the user stays logged in.
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    onLogout();
    // Full reload so the auth context re-fetches and lands at /login.
    window.location.href = "/login";
  };

  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto mt-8" data-testid="profile-page">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            className={cn(
              "px-4 py-2.5 text-sm font-medium transition-colors relative",
              activeTab === "profile"
                ? "text-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            )}
            onClick={() => setActiveTab("profile")}
            data-testid="tab-profile"
          >
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Profile
            </div>
            {activeTab === "profile" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
            )}
          </button>
          <button
            className={cn(
              "px-4 py-2.5 text-sm font-medium transition-colors relative",
              activeTab === "connections"
                ? "text-indigo-600"
                : "text-gray-500 hover:text-gray-700"
            )}
            onClick={() => setActiveTab("connections")}
            data-testid="tab-connections"
          >
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Connections
            </div>
            {activeTab === "connections" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600" />
            )}
          </button>
        </div>

        {activeTab === "profile" && (
          <div className="max-w-lg">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-16 w-16 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xl font-semibold">
                  {initials}
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900" data-testid="text-profile-name">{user.name}</p>
                  <p className="text-sm text-gray-500" data-testid="text-profile-email">{user.email}</p>
                </div>
              </div>

              <div className="space-y-3 border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>Name</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{user.name}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span className="h-4 w-4 text-gray-400 text-center">@</span>
                    <span>Email</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{user.email}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <Globe className="h-4 w-4 text-gray-400" />
                    <span>Timezone</span>
                  </div>
                  <Popover open={tzOpen} onOpenChange={setTzOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-sm h-8 max-w-[240px] truncate"
                        data-testid="select-timezone"
                      >
                        {currentTz.replace(/_/g, " ")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-2" align="end">
                      <Input
                        placeholder="Search timezone..."
                        value={tzSearch}
                        onChange={e => setTzSearch(e.target.value)}
                        className="h-8 text-sm mb-2"
                        data-testid="input-timezone-search"
                      />
                      <div className="max-h-60 overflow-y-auto space-y-0.5">
                        {filteredTimezones.map(tz => (
                          <button
                            key={tz}
                            onClick={() => {
                              timezoneMutation.mutate(tz);
                              setTzOpen(false);
                              setTzSearch("");
                            }}
                            className={cn(
                              "w-full text-left px-2 py-1.5 text-xs rounded-md transition-colors flex items-center justify-between",
                              currentTz === tz ? "bg-indigo-50 text-indigo-700" : "hover:bg-gray-50 text-gray-700"
                            )}
                            data-testid={`timezone-option-${tz}`}
                          >
                            <span className="truncate">{getTimezoneLabel(tz)}</span>
                            {currentTz === tz && <Check className="h-3 w-3 flex-shrink-0 ml-1" />}
                          </button>
                        ))}
                        {filteredTimezones.length === 0 && (
                          <p className="text-xs text-gray-400 text-center py-2">No timezones found</p>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="border-t border-gray-100 mt-4 pt-4">
                <Button
                  variant="outline"
                  className="w-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  onClick={handleLogout}
                  data-testid="button-logout"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "connections" && (
          <ConnectionsContent />
        )}
      </div>
    </Layout>
  );
}
